import useSEO from '@/hooks/useSEO';
import { calculateFAQs } from './data';
import SpreadStepSection from './components/SpreadStepSection';
import CommissionStepSection from './components/CommissionStepSection';
import SwapStepSection from './components/SwapStepSection';
import TotalFormulaSection from './components/TotalFormulaSection';
import CalculateFAQSection from './components/CalculateFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function CalculateForexTradingCosts() {
  useSEO({
    /* @replace:calc-title */

    title: 'Forex Trading Cost Calculator: Spread, Commission and Swap',
    /* @replace:calc-description */

    description: 'Calculate the total cost of a forex trade using spread, commission, lot size, pip value, swap rate and holding period in one interactive guide.',
    /* @replace:calc-keywords */

    keywords: 'forex trading cost calculator, spread calculator, commission calculator, swap calculator, forex cost formula, pip value',
    /* @replace:calc-canonical */

    canonical: '/calculate-forex-trading-costs/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: calculateFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero — magazine editorial layout, split: left title / right formula */}
      <section className="relative w-full overflow-hidden bg-foreground-950">
        {/* Subtle geometric grid — Bloomberg-style structure */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />

        {/* Single warm accent glow — top-right, behind the formula */}
        <div className="absolute top-[-120px] right-[-80px] w-[640px] h-[500px] rounded-full opacity-[0.07] blur-[150px] bg-accent-500" />

        {/* Faint primary glow — bottom-left, behind the title */}
        <div className="absolute bottom-[-100px] left-[-60px] w-[500px] h-[400px] rounded-full opacity-[0.05] blur-[140px] bg-primary-500" />

        {/* Top accent hairline */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-accent-500/0 via-accent-500/30 to-accent-500/0" />

        <div className="relative max-w-[1100px] mx-auto px-4 md:px-10 pt-14 md:pt-32 pb-14 md:pb-28">
          <div className="flex flex-col lg:flex-row lg:items-center gap-8 md:gap-16 lg:gap-20">
            {/* Left column — title & context */}
            <div className="flex-1 lg:max-w-[440px]">
              <h1 className="text-[30px] md:text-[48px] lg:text-[56px] font-[750] text-background-50 leading-[1.06] tracking-[-0.02em] mb-4 md:mb-6">
                How to Calculate
                <br />
                <span className="text-accent-400">Forex Trading</span>
                <br />
                Costs
              </h1>

              <p className="text-[14px] md:text-[15px] text-foreground-400 leading-relaxed max-w-[380px] mb-0">
                Three costs hit your trade at different moments. Know the formula — know your number.
              </p>

              {/* Subtle accent divider */}
              <div className="hidden lg:block mt-8 w-12 h-[3px] bg-accent-500/40 rounded-full" />
            </div>

            {/* Right column — the formula as monumental typography */}
            <div className="flex-1 lg:max-w-[540px]">
              <div className="space-y-4 md:space-y-7">
                {/* Spread */}
                <div className="flex items-baseline gap-4 md:gap-7">
                  <span className="text-[34px] md:text-[56px] lg:text-[64px] font-[750] text-primary-400 leading-none tracking-[-0.03em]">
                    Spread
                  </span>
                  <span className="text-[12px] md:text-[14px] text-foreground-500 leading-tight pb-1">
                    × Lots × $/pip
                    <br />
                    <span className="text-foreground-700 text-[11px]">entry toll</span>
                  </span>
                </div>

                {/* Plus */}
                <div className="flex items-center gap-3 pl-2">
                  <span className="text-[22px] md:text-[34px] font-[200] text-foreground-700 leading-none">+</span>
                  <div className="flex-1 h-[1px] bg-foreground-800/40" />
                </div>

                {/* Commission */}
                <div className="flex items-baseline gap-4 md:gap-7">
                  <span className="text-[34px] md:text-[56px] lg:text-[64px] font-[750] text-accent-400 leading-none tracking-[-0.03em]">
                    Commission
                  </span>
                  <span className="text-[12px] md:text-[14px] text-foreground-500 leading-tight pb-1">
                    × 2 × Lots
                    <br />
                    <span className="text-foreground-700 text-[11px]">broker cut</span>
                  </span>
                </div>

                {/* Plus */}
                <div className="flex items-center gap-3 pl-2">
                  <span className="text-[22px] md:text-[34px] font-[200] text-foreground-700 leading-none">+</span>
                  <div className="flex-1 h-[1px] bg-foreground-800/40" />
                </div>

                {/* Swap */}
                <div className="flex items-baseline gap-4 md:gap-7">
                  <span className="text-[34px] md:text-[56px] lg:text-[64px] font-[750] text-secondary-400 leading-none tracking-[-0.03em]">
                    Swap
                  </span>
                  <span className="text-[12px] md:text-[14px] text-foreground-500 leading-tight pb-1">
                    × Lots × Days
                    <br />
                    <span className="text-foreground-700 text-[11px]">overnight</span>
                  </span>
                </div>

                {/* Equals line */}
                <div className="flex items-center gap-4 pt-2 border-t border-foreground-800/30">
                  <span className="text-[26px] md:text-[40px] font-[200] text-foreground-500 leading-none">=</span>
                  <span className="text-[24px] md:text-[36px] lg:text-[40px] font-[750] text-background-50 leading-none tracking-[-0.02em]">
                    Total Cost
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SpreadStepSection />
      <CommissionStepSection />
      <SwapStepSection />
      <TotalFormulaSection />
      <CalculateFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}